import { useMemo } from 'react'
import { useEmploymentProfiles } from '@/features/employee-management/hooks/useEmploymentProfiles'
import { Shift } from '../types/schedule.model'
import { useScheduleStore } from '../store/useScheduleStore'
import {
  DateRange,
  PayrollRow,
  buildPayrollRows,
  rowsMissingRate,
  rowsWithUnmodelledPremiums
} from '../utils/payroll'

/**
 * Payroll rows plus the caveats that go with them.
 *
 * Lives in a hook rather than inside the table because the warnings belong
 * above the view switcher, outside the component that renders the numbers -
 * and both need the same computation.
 */
export const usePayrollRows = (
  range: DateRange,
  employees: Array<{ userId: string; name: string }>,
  shifts: Shift[]
): {
  rows: PayrollRow[]
  missingRate: PayrollRow[]
  withPremiums: PayrollRow[]
  ratesForbidden: boolean
  isLoading: boolean
} => {
  const { profilesByUserId, isLoading, isForbidden } = useEmploymentProfiles()
  const nightStartMinutes = useScheduleStore(state => state.nightStartMinutes)

  const rows = useMemo(
    () =>
      buildPayrollRows(
        employees,
        shifts,
        profilesByUserId,
        range,
        undefined,
        nightStartMinutes
      ),
    [employees, shifts, profilesByUserId, range, nightStartMinutes]
  )

  return useMemo(
    () => ({
      rows,
      missingRate: rowsMissingRate(rows),
      withPremiums: rowsWithUnmodelledPremiums(rows),
      // Same reason the rows go out unnamed when the fetch is forbidden: an
      // absent rate means "not set" only when the rates could be read.
      ratesForbidden: isForbidden,
      // Rates arrive with the profiles, so until they do every row would look
      // like an employee nobody set a rate for.
      isLoading
    }),
    [rows, isForbidden, isLoading]
  )
}
